import React, {useState, useContext, useEffect} from 'react';
// import {Link, Redirect} from 'react-router-dom';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as R from 'ramda';
import {Maybe} from 'monet';

import Form from '@/Form';
import Text from '@/Form/Text';
import api from '@/api';
import FeatherLight from '@/FeatherLight';
import {userCtx, userDispatchCtx} from '@/auth';
import logo from '@/assets/images/logo.png';
import ipcLogo from '@/assets/images/ipc_logo.png';
import absgLogo from '@/assets/images/absg_logo.svg';
import {LocaleLink} from '@/routes';
import {appHost, appBaseName, cmsSetting, vendorServer} from '@/config';
import MainSiteLink from '@/components/MainSiteLink';
import {contactUs, faq, home} from '@/contexts/support-link';

import {disclaimer, privacy} from '@/views/Login/wordings';
import styles from '@/views/Login/index.module.css';
import LoadingOverlay from 'react-loading-overlay';

import {SystemConfigCtx} from '@/contexts';
import {blank} from '@/util';

const metaFields = ['errors'];

const LoginInput = ({formData, setFormData, dispatch, handleErrorMsg}) => {
  const {siteCode} = useContext(SystemConfigCtx);
  const {t: tr, i18n: i18nCtx} = useTranslation('login');

  const handleSubmit = event => {
    event.preventDefault();
    setFormData(R.assoc('redirectFlagfromLogin', true)); // update to handle redirect

    api
      .post('/login', R.omit(metaFields, formData))
      .then(res => dispatch({type: 'me', payload: res.data}))
      .catch(res =>
        setFormData(
          R.assoc('errors', {base: [handleErrorMsg(res.response.data.error)]})
        )
      );
  };

  return (
    <Form
      id='login-form-input'
      stateHook={[formData, setFormData]}
      onSubmit={handleSubmit}
      style={{height: 'auto'}}
    >
      {siteCode === 'absg' && (
        <h1 className={styles.titleAbsg}>Advisor Login </h1>
      )}
      <Text
        name='login'
        placeholder={`${
          tr(formData.area === 'b2b' ? 'login_b2b' : 'login')[siteCode]
        }`}
        required
      />
      {formData.area === 'b2c' && (
        /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
        <label className='instruction' data-help={tr('loginHelpB2c')} />
      )}
      <Text
        type='password'
        name='password'
        placeholder={tr('password')}
        required
      />
      {formData.area === 'b2c' && (
        /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
        <label className='instruction' data-help={tr('passwordHelpB2c')} />
      )}
      <p style={{textAlign: 'center'}}>
        {tr('loginWording')}
        <br />
        <FeatherLight
          content={`${disclaimer(siteCode)[i18nCtx.language]}`}
          label={tr('disclaimer')}
        />
        {tr('and')}
        <FeatherLight
          content={`${privacy(siteCode)[i18nCtx.language]}`}
          label={tr('privacyPolicy')}
        />
        {tr('loginWording2')}
      </p>
      <p style={{marginTop: '30px', textAlign: 'center'}}>
        <input type='submit' value={tr('login_btn')} />
      </p>
    </Form>
  );
};

const OtpInput = ({formData, setFormData, dispatch, handleErrorMsg}) => {
  const [flashData, setFlashData] = useState('');
  const {t: tr} = useTranslation('login');

  useEffect(() => {
    setFormData(R.assoc('errors', ''));
  }, []);

  const handleSubmit = event => {
    event.preventDefault();

    setFlashData(''); // clean up
    setFormData(R.assoc('redirectFlagfromLogin', true)); // update to handle redirect
    setFormData(R.assoc('loading', true));

    api
      .post('/login', R.omit(metaFields, formData))
      .then(res => dispatch({type: 'me', payload: res.data}))
      .catch(res =>
        setFormData(
          R.assoc('errors', {base: [handleErrorMsg(res.response.data.error)]})
        )
      )
      .finally(() => setFormData(R.assoc('loading', false)));
  };

  const handleResend = event => {
    event.preventDefault();

    // clean up
    setFlashData('');
    setFormData(R.assoc('errors', ''));
    setFormData(R.assoc('loading', true));

    api
      .post('/resend_otp', R.omit(metaFields, formData))
      .then(({data}) => {
        const error = R.propOr({}, 'error')(data);
        if (blank(error)) {
          setFlashData(data.msg);
        }
      })
      .catch(res => {
        setFormData(
          R.assoc('errors', {base: [handleErrorMsg(res.response.data.error)]})
        );
      })
      .finally(() => setFormData(R.assoc('loading', false)));
  };

  const handleCancel = event => {
    event.preventDefault();

    // clean up
    setFlashData('');
    setFormData(R.assoc('errors', ''));
    setFormData(R.assoc('loading', true));

    api.post('/cancel_otp', R.omit(metaFields, formData)).finally(() => {
      window.location.href = `${appBaseName}/en/login`;
    });
  };

  const ResultMsg = () =>
    Maybe.fromFalsy(flashData)
      .map(val => <div className='alert alert-success'>{val}</div>)
      .orNull();

  return (
    <Form
      id='login-form-input'
      stateHook={[formData, setFormData]}
      onSubmit={handleSubmit}
      style={{height: 'auto'}}
    >
      <LoadingOverlay active={formData.loading} spinner>
        <ResultMsg />
        <h1 className={styles.titleAbsg}>OTP Code</h1>
        <LocaleLink to='/' onClick={handleResend}>
          Resend
        </LocaleLink>
        <Text name='otpCode' placeholder={tr('otp_code')} />
        <p style={{marginTop: '30px', textAlign: 'center'}}>
          <input type='submit' value={tr('otp_btn')} />
        </p>
        <LocaleLink to='/' onClick={handleCancel}>
          Cancel
        </LocaleLink>
      </LoadingOverlay>
    </Form>
  );
};

const Login = ({match, location: {state: {referrer} = {}}}) => {
  const user = useContext(userCtx);
  const dispatch = useContext(userDispatchCtx);
  const currentYear = new Date().getFullYear();
  const {siteCode, siteName = ''} = useContext(SystemConfigCtx);
  const {t: tr, i18n: i18nCtx} = useTranslation('login');
  const inquiryEmail = R.path(['inquiry', 'jaEmail'], cmsSetting);

  const [formData, setFormData] = useState({
    login: '',
    password: '',
    otpCode: '',
    redirectFlagfromLogin: false, // default to normal redirect b2c or b2b
    referrer,
    area: '', // default to b2c area
    loading: false,
  });

  useEffect(
    () => {
      document.body.classList.add(`bg-login-${siteCode}`);
      document.body.classList.add('bg-login');
      document.body.classList.add('container');
      if (siteCode === 'ipc' || siteCode === 'absg') {
        setFormData(R.assoc('area', 'b2b'));
      }

      return () => {
        document.body.classList.remove(`bg-login-${siteCode}`);
        document.body.classList.remove('bg-login');
        document.body.classList.remove('container');
      };
    },
    [siteCode] // add back siteCode monitoring because sometime first load is undefined for siteCode
  );

  const handleErrorMsg = error => {
    if (formData.area === 'b2b') {
      return error.b2b;
    }
    if (formData.area === 'b2c') {
      return error.b2c;
    }
    return null;
  };

  const handleChangeArea = event => {
    event.preventDefault();

    const area = event.target.getAttribute('data-area');
    setFormData(R.assoc('area', area)); // update to handle area display
  };

  // should redirect back to main b2b website at first login for producer
  if (user.currentProducer && formData.redirectFlagfromLogin) {
    const returnPath = {
      afs: `${appHost}/b2b/${i18nCtx.language}`,
      ipc: `${appHost}/b2b/${i18nCtx.language}`,
      absg: `${vendorServer}`,
    };

    window.location.href = returnPath[siteCode];
    return null;
  }

  if (user.currentProducer || user.currentClient) {
    // FIXME: unknow error, temporary back to pure b2c/b2b redirect
    // const composedPath = path =>
    //   `/${R.pathOr('en', ['params', 'locale'], match)}/${R.propOr(
    //     path,
    //     'referrer',
    //     formData
    //   )}`;
    const composedPath = path =>
      `/${R.pathOr('en', ['params', 'locale'], match)}/${path}`;

    const redirectPath = R.cond([
      [R.has('currentProducer'), R.always(composedPath('b2b'))],
      [R.has('currentClient'), R.always(composedPath('b2c'))],
      [R.T, R.always('/en/login')],
    ]);

    // return <Redirect to={redirectPath(user)} />;
    window.location.href = `${appBaseName}${redirectPath(user)}`;
    return null;
  }

  const B2cLink = () => (
    <p>
      <span>{tr('newUser')}</span>
      <LocaleLink to='/registration'> {tr('activateYourAccount')}</LocaleLink>
      <span>{tr('newUser2')}</span>
      <br />
      <span>{tr('cannotLogin')}</span>
      <LocaleLink to='/forget_password'>
        {' '}
        {tr('forgetLoginIdPassword')}
      </LocaleLink>
      <span>{tr('cannotLogin2')}</span>
    </p>
  );

  const B2bLink = () => (
    <p>
      <span>{tr('cannotLogin_b2b')}</span>
      <LocaleLink to='/forget_password_b2b'> {tr('forgetPassword')}</LocaleLink>
      <span>{tr('cannotLogin2')}</span>
    </p>
  );

  const LangAndCommonLink = () => (
    <>
      <section className='link'>
        <div className='lang'>
          <Link
            to='/en/login'
            className={i18nCtx.language === 'en' ? 'active' : ''}
          >
            <span>ENGLISH</span>
          </Link>
          <Link
            to='/zh/login'
            className={i18nCtx.language === 'zh' ? 'active' : ''}
          >
            <span>中文</span>
          </Link>
          {siteCode === 'afs' && (
            <>
              <Link
                to='/ja/login'
                className={i18nCtx.language === 'ja' ? 'active' : ''}
              >
                <span>日本語</span>
              </Link>
              <Link
                to='/kr/login'
                className={i18nCtx.language === 'kr' ? 'active' : ''}
              >
                <span>한국어</span>
              </Link>
            </>
          )}
        </div>
        <hr />
      </section>
      <section className='help'>
        {siteCode === 'afs' && (
          <>
            <p>{tr('needHelp')}</p>
            <p>
              {tr('faq_1')}
              <MainSiteLink href={faq(siteCode)}>{tr('faq_2')}</MainSiteLink>
              {tr('faq_3')}
            </p>
            <p>
              {tr('needAssistant')}
              <MainSiteLink href={contactUs(siteCode)}>
                {tr('contactUs')}
              </MainSiteLink>
            </p>
          </>
        )}
        {siteCode === 'ipc' && (
          <>
            <p>
              {tr('needHelpPlease')}
              <MainSiteLink href={contactUs(siteCode)}>
                {tr('contactUs')}
              </MainSiteLink>
            </p>
          </>
        )}
        {i18nCtx.language === 'ja' && (
          <p>
            {tr('inquiry_1')}
            <a
              href={`mailto:${inquiryEmail}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {`${inquiryEmail}`}
            </a>
            {tr('inquiry_2')}
          </p>
        )}
      </section>
      <section className='copyright'>
        @ {currentYear} {siteName.toUpperCase()}
      </section>
    </>
  );

  const LangAndCommonLinkAbsg = () => (
    <>
      <section className='copyright'>
        @ {currentYear} {siteName.toUpperCase()}
      </section>
    </>
  );

  const SelectArea = () => (
    <p className={styles.select_area}>
      <strong>{tr('select_area')}</strong>
      <br />
      <input
        type='submit'
        onClick={handleChangeArea}
        data-area='b2c'
        value={tr('select_client')}
        className={styles.select_button}
      />
      <br />
      <br />
      <input
        type='submit'
        onClick={handleChangeArea}
        data-area='b2b'
        value={tr('select_producer')}
        className={styles.select_button}
      />
    </p>
  );

  if (blank(siteCode)) {
    return null;
  }

  const styleClass = {
    afs: styles.wrapLoginAfs,
    ipc: styles.wrapLoginIpc,
    absg: styles.wrapLoginAbsg,
  };

  return (
    <div className={`${styleClass[siteCode]}`}>
      <main id='login-form'>
        <MainSiteLink href={home}>
          {siteCode === 'afs' && (
            <img id='login-logo-afs' alt='Athena Best' src={logo} />
          )}
          {siteCode === 'ipc' && (
            <img id='login-logo-ipc' alt='IPC' src={ipcLogo} />
          )}
          {siteCode === 'absg' && (
            <img id='login-logo-absg' alt='ABSG' src={absgLogo} />
          )}
        </MainSiteLink>

        {formData.area === '' && siteCode === 'afs' && <SelectArea />}

        {formData.area !== '' && user.otpUserId && (
          <OtpInput
            formData={formData}
            setFormData={setFormData}
            dispatch={dispatch}
            handleErrorMsg={handleErrorMsg}
          />
        )}

        {formData.area !== '' && !user.otpUserId && (
          <LoginInput
            formData={formData}
            setFormData={setFormData}
            dispatch={dispatch}
            handleErrorMsg={handleErrorMsg}
          />
        )}

        <section style={{textAlign: 'center'}}>
          {formData.area === 'b2c' && <B2cLink />}
          {formData.area === 'b2b' && siteCode !== 'absg' && <B2bLink />}
        </section>
        {formData.area !== '' && siteCode !== 'absg' && <LangAndCommonLink />}
        {formData.area !== '' && siteCode === 'absg' && (
          <LangAndCommonLinkAbsg />
        )}
      </main>
    </div>
  );
};

export default Login;
